/**
 * My Clients (LP-11): the rules for the care manager's client list. Its
 * order is byDisplayName (src/lib/clientOrder.ts): everyone by first name,
 * as All clients orders them (LP-19).
 *
 *   detailLine      the row's one detail line: the next visit (an
 *                   appointment or a care manager's planned visit) or SPICE
 *                   session, whichever comes first, then the last note's
 *                   day, on every row. With nothing scheduled nothing is
 *                   said about a visit, never "No visit scheduled" (many
 *                   care managers keep a paper planner); a client with no
 *                   visit and no note has a blank line.
 *   stayChip        the record chip for where the client is staying.
 */
import type { CmHomeClientWire } from '@/lib/cmHomeApi';
import { proseDay, proseUpcomingDay } from '@/lib/proseDate';

interface Upcoming {
  kind: 'Visit' | 'Session';
  date: string;
  time: string | null;
}

function nextScheduled(c: Pick<CmHomeClientWire, 'next_visit' | 'next_care_visit' | 'spice_row'>): Upcoming | null {
  const options: Upcoming[] = [];
  if (c.next_visit) options.push({ kind: 'Visit', date: c.next_visit.date, time: c.next_visit.time });
  // A care manager's planned visit is a visit too.
  if (c.next_care_visit) options.push({ kind: 'Visit', date: c.next_care_visit.date, time: c.next_care_visit.time });
  const session = c.spice_row?.next_session ?? null;
  if (session) options.push({ kind: 'Session', date: session.date, time: session.start_time ? session.start_time.slice(0, 5) : null });
  options.sort((a, b) => a.date.localeCompare(b.date) || (a.time ?? '').localeCompare(b.time ?? ''));
  return options[0] ?? null;
}

export function detailLine(
  c: Pick<CmHomeClientWire, 'next_visit' | 'next_care_visit' | 'spice_row' | 'last_note_occurred_on'>,
  todayIso: string,
): string {
  const next = nextScheduled(c);
  const parts: string[] = [];
  // Nothing scheduled says nothing about a visit (no "No visit scheduled").
  if (next !== null) parts.push(`${next.kind} ${proseUpcomingDay(next.date, todayIso)}${next.time ? ` at ${next.time}` : ''}`);
  if (c.last_note_occurred_on) parts.push(`Last note ${proseDay(c.last_note_occurred_on, todayIso)}`);
  return parts.join(' · ');
}

export function stayChip(c: Pick<CmHomeClientWire, 'stay'>): { key: 'hospital' | 'hospice'; text: string } | null {
  if (c.stay === 'hospital') return { key: 'hospital', text: 'Hospital' };
  if (c.stay === 'hospice') return { key: 'hospice', text: 'On hospice' };
  return null;
}

/** "1 client" / "5 clients". */
export function countPhrase(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}
